import { useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod/v4';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import AvatarIcon from '@interxfi/design-system/icons/react/icon-avatar';
import { type I18nNamespaces, type TFunction, useTranslation } from '@interxfi/translation';
import {
  IxcRegistrationV1AccountReviewType,
  type IxcRegistrationV1ClearingControls,
  type IxcRegistrationV1ClearingControlsSettings,
  IxcRegistrationV1ClearingControlsSettingsCipProgram,
  IxcRegistrationV1ControlsMarginType,
  IxcRegistrationV1RegistrationAccountType,
} from '@interxfi/types/generated/api';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppEnumMultiSelectInputWithLens } from '../../../../../../components/form/app-enum-multi-select-input';
import { AppEnumSelectInputWithLens } from '../../../../../../components/form/app-enum-select-input';
import { AppSelectInputWithLens } from '../../../../../../components/form/app-select-input';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { addDefinedValues } from '../../../../../../util/has-value';
import {
  formValuesToStatesAndTerritories,
  getInitialFormValuesForRegistrationStatesAndTerritories,
  StateAndTerritoriesForm,
  type StateAndTerritoriesFormValues,
  stateAndTerritoriesFormSchema,
} from './state-and-territories-form';

export const programControlsRegistrationFormSchema = z.object({
  accounts: z.object({
    canCreate: z.object({
      value: z.boolean(),
      types: z.array(z.enum(IxcRegistrationV1RegistrationAccountType)),
    }),
    marginType: z.enum(IxcRegistrationV1ControlsMarginType).or(z.literal('')),
  }),
  accountReviewType: z.enum(IxcRegistrationV1AccountReviewType).or(z.literal('')),
  specialPurposeAccountReviewType: z.enum(IxcRegistrationV1AccountReviewType).or(z.literal('')),
  cipProgram: z.enum(IxcRegistrationV1ClearingControlsSettingsCipProgram).or(z.literal('')),
  states: stateAndTerritoriesFormSchema,
  optionalSuitabilityQuestions: z.boolean(),
});

export type ProgramControlsRegistrationFormValues = z.infer<typeof programControlsRegistrationFormSchema>;

export const programControlsRegistrationFormDefaultValues: ProgramControlsRegistrationFormValues = {
  accounts: {
    canCreate: {
      value: false,
      types: [],
    },
    marginType: '',
  },
  accountReviewType: '',
  specialPurposeAccountReviewType: '',
  cipProgram: '',
  states: { kind: '' } as unknown as StateAndTerritoriesFormValues, // Initialize it with an empty value
  optionalSuitabilityQuestions: false,
};

function allowedSpecialPurposeAccountReviewTypeFilter(value: IxcRegistrationV1AccountReviewType) {
  return ![IxcRegistrationV1AccountReviewType.DualManual, IxcRegistrationV1AccountReviewType.DualAutomated].includes(value);
}

export function getInitialFormValuesForProgramRegistrationControls(
  data: IxcRegistrationV1ClearingControls | undefined,
): ProgramControlsRegistrationFormValues {
  if (!data) {
    return programControlsRegistrationFormDefaultValues;
  }

  return {
    accounts: {
      canCreate: {
        value: Boolean(data?.settings?.canOpenAccounts),
        types: data?.settings?.accountTypes ?? [],
      },
      marginType: data?.settings?.marginType ?? '',
    },
    accountReviewType: data?.settings?.accountReviewType ?? '',
    specialPurposeAccountReviewType: data?.settings?.specialPurposeAccountReviewType ?? '',
    cipProgram: data?.settings?.cipProgram ?? '',
    optionalSuitabilityQuestions: Boolean(data?.settings?.optionalSuitabilityQuestions),
    states: getInitialFormValuesForRegistrationStatesAndTerritories(data?.settings?.states),
  };
}

function formValuesToProgramRegistrationControlsSettings(values: ProgramControlsRegistrationFormValues): IxcRegistrationV1ClearingControlsSettings {
  return addDefinedValues<IxcRegistrationV1ClearingControlsSettings>(
    {
      canOpenAccounts: values.accounts.canCreate.value,
      optionalSuitabilityQuestions: values.optionalSuitabilityQuestions,
      states: formValuesToStatesAndTerritories(values.states) ?? [],
    },
    {
      marginType: values.accounts.marginType || undefined,
      accountReviewType: values.accountReviewType || undefined,
      specialPurposeAccountReviewType: values.specialPurposeAccountReviewType || undefined,
      cipProgram: values.cipProgram || undefined,
      accountTypes: values.accounts.canCreate.types.length > 0 ? values.accounts.canCreate.types : undefined,
    },
  );
}

export function formValuesToProgramRegistrationControls(values: ProgramControlsRegistrationFormValues): IxcRegistrationV1ClearingControls {
  const settings = formValuesToProgramRegistrationControlsSettings(values);

  return addDefinedValues<IxcRegistrationV1ClearingControls>(
    {},
    {
      settings: Object.values(settings).filter(Boolean).length > 0 ? settings : undefined,
    },
  );
}

function getMarginTypeOptions(t: TFunction): InputDropdownOption[] {
  return [
    {
      value: IxcRegistrationV1ControlsMarginType.Cash,
      label: t(`ixcRegistrationV1:enum.IxcRegistrationV1ControlsMarginType.${IxcRegistrationV1ControlsMarginType.Cash}`),
    },
    {
      value: IxcRegistrationV1ControlsMarginType.Lpm,
      label: 'Limited Purpose Margin (LPM)',
    },
  ];
}

const i18nNamespaces: I18nNamespaces[] = ['ixmUserauthV1', 'ixcRegistrationV1'] as const;

function MarginTypeSelect({ lens }: { lens: Lens<IxcRegistrationV1ControlsMarginType | ''> }) {
  const { t } = useTranslation(i18nNamespaces);
  const marginTypeOptions = useMemo(() => getMarginTypeOptions(t), [t]);
  const selectedMarginType = useWatch(lens.interop());

  return (
    <AppSelectInputWithLens
      info={match(selectedMarginType)
        .with(
          IxcRegistrationV1ControlsMarginType.Cash,
          () => 'The Program can only create Cash accounts, meaning only settled balances are available to trade.',
        )
        .with(IxcRegistrationV1ControlsMarginType.Lpm, () => (
          <span>
            The Program can create Limited Purpose Margin (LPM) <em>and</em> Cash accounts. LPM means unsettled proceeds are available to trade.
          </span>
        ))
        .otherwise(() => undefined)}
      label="Margin Type"
      lens={lens}
      options={marginTypeOptions}
    />
  );
}

interface ProgramRegistrationControlsFormProps {
  lens: Lens<ProgramControlsRegistrationFormValues>;
}

export function ProgramRegistrationControlsForm({ lens }: ProgramRegistrationControlsFormProps) {
  const canOpenAccounts = useWatch(lens.focus('accounts.canCreate.value').interop());

  return (
    <AccordionFormGroup icon={<AvatarIcon aria-hidden />} title="Registration">
      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Open Accounts" lens={lens.focus('accounts.canCreate.value')} />}
      >
        {canOpenAccounts ? (
          <AppEnumMultiSelectInputWithLens
            enumName="IxcRegistrationV1RegistrationAccountType"
            enumValue={IxcRegistrationV1RegistrationAccountType}
            i18nNamespace="ixcRegistrationV1"
            label="Account Types"
            lens={lens.focus('accounts.canCreate.types')}
          />
        ) : null}
      </AccordionFormGroup.Section>

      <AccordionFormGroup.Section>
        <MarginTypeSelect lens={lens.focus('accounts.marginType')} />

        <StateAndTerritoriesForm lens={lens.focus('states')} />

        <AppEnumSelectInputWithLens
          enumName="IxcRegistrationV1AccountReviewType"
          enumValue={IxcRegistrationV1AccountReviewType}
          i18nNamespace="ixcRegistrationV1"
          label="Account Review Type"
          lens={lens.focus('accountReviewType')}
        />

        <AppEnumSelectInputWithLens
          enumName="IxcRegistrationV1AccountReviewType"
          enumValue={IxcRegistrationV1AccountReviewType}
          filter={allowedSpecialPurposeAccountReviewTypeFilter}
          i18nNamespace="ixcRegistrationV1"
          label="Special Purpose Account Review Type"
          lens={lens.focus('specialPurposeAccountReviewType').cast<IxcRegistrationV1AccountReviewType>()}
        />

        <AppEnumSelectInputWithLens
          enumName="IxcRegistrationV1ClearingControlsSettingsCipProgram"
          enumValue={IxcRegistrationV1ClearingControlsSettingsCipProgram}
          i18nNamespace="ixcRegistrationV1"
          label="CIP Program"
          lens={lens.focus('cipProgram')}
        />
      </AccordionFormGroup.Section>

      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Optional Suitability Questions" lens={lens.focus('optionalSuitabilityQuestions')} />
        }
      />
    </AccordionFormGroup>
  );
}
